import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { inventoryAPI } from '../../services';
import QtyDual from '../../utils/inventoryUnit';

const TYPE_LABELS = {
  IMPORT: { label: 'Nhập kho', variant: 'success' },
  AUTO_DEDUCT: { label: 'Bán hàng', variant: 'info' },
  ADJUSTMENT: { label: 'Kiểm kê', variant: 'warning' },
  TRANSFER_IN: { label: 'Chuyển đến', variant: 'primary' },
  TRANSFER_OUT: { label: 'Chuyển đi', variant: 'secondary' },
  REVERSAL: { label: 'Hoàn tác', variant: 'dark' },
  PRODUCTION_IN: { label: 'SX nhập (BTP)', variant: 'success' },
  PRODUCTION_OUT: { label: 'SX trừ NL', variant: 'warning' },
};

const TYPE_OPTIONS = [
  { value: '', label: 'Tất cả' },
  { value: 'IMPORT', label: 'Nhập kho' },
  { value: 'AUTO_DEDUCT', label: 'Bán hàng (trừ công thức)' },
  { value: 'ADJUSTMENT', label: 'Kiểm kê' },
  { value: 'TRANSFER_IN', label: 'Chuyển đến' },
  { value: 'TRANSFER_OUT', label: 'Chuyển đi' },
  { value: 'REVERSAL', label: 'Hoàn tác' },
  { value: 'PRODUCTION_IN', label: 'Sản xuất nhập' },
  { value: 'PRODUCTION_OUT', label: 'Sản xuất trừ' },
];

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString('vi-VN', { hour12: false }) : '—';

function describeSource(t) {
  switch (t.type) {
    case 'IMPORT': {
      const parts = [];
      if (t.supplier?.name) parts.push(`NCC: ${t.supplier.name}`);
      if (t.invoice_number) parts.push(`HĐ: ${t.invoice_number}`);
      if (t.unit_price != null) {
        const price = Number(t.unit_price).toLocaleString('vi-VN');
        parts.push(`Đơn giá: ${price}đ/${t.material?.unit ?? ''}`);
      }
      return parts.join(' · ') || '—';
    }
    case 'AUTO_DEDUCT':
      return t.order_id ? `Đơn hàng: ${String(t.order_id).slice(0, 8)}…` : '—';
    case 'ADJUSTMENT': {
      const emp = t.stocktake_session?.employee?.full_name;
      return emp ? `NV kiểm kê: ${emp}` : 'Phiên kiểm kê';
    }
    case 'TRANSFER_IN':
      return t.related_branch ? `Từ: ${t.related_branch.name}` : '—';
    case 'TRANSFER_OUT':
      return t.related_branch ? `Đến: ${t.related_branch.name}` : '—';
    default:
      return '—';
  }
}

export default function MaterialTransactionsModal({ show, onHide, branchId, material }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');

  const fetchTransactions = useCallback(async () => {
    if (!branchId || !material?.id) return;
    try {
      setLoading(true);
      const params = { material_id: material.id };
      if (typeFilter) params.type = typeFilter;
      const data = await inventoryAPI.getTransactions(branchId, params);
      setTransactions(Array.isArray(data) ? data : data?.data || []);
    } catch {
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [branchId, material?.id, typeFilter]);

  useEffect(() => {
    if (show) fetchTransactions();
  }, [show, fetchTransactions]);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Lịch sử giao dịch — <strong>{material?.name || ''}</strong>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex align-items-center gap-2 mb-3">
          <Form.Label className="mb-0 small text-muted">Loại:</Form.Label>
          <Form.Select
            size="sm"
            style={{ width: 220 }}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
          <span className="text-muted small ms-auto">
            {transactions.length} giao dịch (200 gần nhất)
          </span>
        </div>

        {loading ? (
          <div className="text-center py-5"><Spinner /></div>
        ) : transactions.length === 0 ? (
          <p className="text-muted text-center py-4">Chưa có giao dịch nào</p>
        ) : (
          <Table bordered hover responsive size="sm">
            <thead className="table-light">
              <tr>
                <th>Thời gian</th>
                <th>Loại</th>
                <th className="text-end">Số lượng</th>
                <th className="text-end">Tồn sau giao dịch</th>
                <th>Nguồn</th>
                <th>Ghi chú</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t) => {
                const meta = TYPE_LABELS[t.type] ?? { label: t.type, variant: 'secondary' };
                const qty = Number(t.quantity);
                const isPositive = qty > 0;
                const mat = t.material ?? material;
                return (
                  <tr key={t.id}>
                    <td className="small">{fmtDateTime(t.created_at)}</td>
                    <td><Badge bg={meta.variant}>{meta.label}</Badge></td>
                    <td className={`text-end fw-medium ${isPositive ? 'text-success' : 'text-danger'}`}>
                      {isPositive ? '+' : ''}
                      <QtyDual quantityInBase={qty} material={mat} />
                    </td>
                    <td className="text-end">
                      <QtyDual quantityInBase={t.balance_after} material={mat} />
                    </td>
                    <td className="small">{describeSource(t)}</td>
                    <td className="small text-muted">{t.note || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Modal.Body>
    </Modal>
  );
}
